import { ReloadOutlined } from "@ant-design/icons"
import { Button, Result, Skeleton } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { NotAllowed } from "../../../../components/PermissionGate/NotAllowed.tsx"
import type { CompanyDefaultsAccessResponse } from "../../types/company-defaults-access.types.ts"
import {
  type CompanyDefaultsScope,
  useCompanyDefaultsAccess,
  useCompanyDefaultsScope
} from "./company-defaults-access.ts"

interface CompanyDefaultsGateProps {
  allows: (
    access: CompanyDefaultsAccessResponse,
    scope: CompanyDefaultsScope
  ) => boolean
  children: ReactNode
}

/** A Company Defaults page, opened only once the server says the user may. */
export function CompanyDefaultsGate({
  allows,
  children
}: CompanyDefaultsGateProps) {
  const { t } = useTranslation()
  const { status, access, retry } = useCompanyDefaultsAccess()
  const scope = useCompanyDefaultsScope()

  if (status === "loading") {
    return (
      <div className="flex-1" data-testid="company-defaults-access-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    )
  }

  if (status === "error" || !access) {
    return (
      <Result
        className="flex-1"
        status="warning"
        title={t("companyDefaultsAccess.loadError.title")}
        subTitle={t("companyDefaultsAccess.loadError.description")}
        extra={
          <Button icon={<ReloadOutlined />} onClick={retry}>
            {t("companyDefaultsAccess.retry")}
          </Button>
        }
      />
    )
  }

  if (!allows(access, scope)) {
    return <NotAllowed description={t("companyDefaultsAccess.denied")} />
  }

  return <>{children}</>
}
